/**
 * WHOSE OVERRIDE IT IS: the account, not the seat.
 *
 * A per-player override lives in `permissions.playerOverrides`, and the key
 * it is filed under has to be a name the SERVER knows. The rules only ever
 * know the caller's account (`request.auth.uid`, `@request.auth.id`); a seat
 * id (`<uid>_<suffix>`) is minted afresh for every tab and every rejoin.
 * Overrides were filed by seat until 2026-10, so the rules never found a
 * grant, and the page did - a player saw the waiting rooms and every read
 * was refused. A seat-keyed override was also lost at the next reload.
 *
 * So overrides are keyed by ACCOUNT. An entry an older build wrote under
 * the seat id is still read, beneath the account's, and `withOverride`
 * moves it across the next time the GM changes that player.
 */
import type { PermissionsDoc } from './permissions';

/**
 * The account a seat belongs to: the seat id up to its first `_`.
 *
 * The same convention the Firestore rules match seats with
 * (`playerId.matches(uid + '_.*')`); an id with no suffix is its own account.
 */
export function accountOf(seatId: string): string {
  return seatId.split('_')[0];
}

/**
 * One player's overrides, as feature -> locked. The account's entry wins a
 * feature both name; a legacy seat-keyed entry fills the gaps. Never null.
 */
export function overridesFor(permissions: PermissionsDoc | null | undefined, seatId: string): Record<string, boolean> {
  const all = permissions?.playerOverrides ?? {};
  return { ...all[seatId], ...all[accountOf(seatId)] };
}
